import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { emoteOwned } from '@il-colpo/meta/progress';
import { EMOTE_IDS, EMOTE_SHOWN_MS, type EmoteId } from '@il-colpo/shared';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import { ownedCosmetics } from '../../meta/owned.ts';
import type { PlayerProgress } from '../../meta/types.ts';
import { getConnection, useConnection } from '../../online/onlineConnection.ts';
import { useNow } from '../../systems/useNow.ts';
import { IconButton } from '../../ui/index.ts';
import { EmoteGlyph } from './EmoteGlyph.tsx';
import styles from './Emotes.module.css';

/**
 * The emotes a player may send: the free ones at once, and the ones their level and trophies have
 * opened as soon as the progress is read (it is a request; nobody waits for it to start talking).
 */
export function useOwnedEmotes(): readonly EmoteId[] {
  const meta = useMetaClient();
  const load = useMemo(() => (meta ? () => meta.progress(Date.now()) : null), [meta]);
  const progress = useRemote<PlayerProgress>(load);
  return useMemo(() => {
    const owned = progress.data ? ownedCosmetics(progress.data) : new Set<string>();
    return EMOTE_IDS.filter((id) => emoteOwned(id, owned));
  }, [progress.data]);
}

/** A row of buttons, one for each emote the player has. */
export function EmoteBar({ onSent }: { onSent?: () => void }) {
  const { t } = useTranslation('online');
  const emotes = useOwnedEmotes();
  return (
    <div className={styles['bar']} role="group" aria-label={t('chat.emotes')}>
      {emotes.map((id) => (
        <button
          key={id}
          type="button"
          className={styles['emoteButton']}
          aria-label={t(`chat.emote.${id}`)}
          title={t(`chat.emote.${id}`)}
          onClick={() => {
            getConnection().emote(id);
            onSent?.();
          }}
        >
          <EmoteGlyph id={id} size="1.7rem" />
        </button>
      ))}
    </div>
  );
}

/** In a heist: a button that opens the emotes above it, and the one that sends one closes it again. */
export function EmoteMenu() {
  const { t } = useTranslation('online');
  const [open, setOpen] = useState(false);
  return (
    <div className={styles['menu']}>
      {open ? (
        <div className={styles['popover']}>
          <EmoteBar
            onSent={() => {
              setOpen(false);
            }}
          />
        </div>
      ) : null}
      <IconButton
        icon="spark"
        label={t('chat.emotes')}
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      />
    </div>
  );
}

/** What the table has just sent, for a few seconds, whoever has the chat closed. */
export function EmoteBubbles() {
  const { t } = useTranslation('online');
  const state = useConnection();
  const now = useNow();
  const recent = state.emotes.filter((e) => now - e.at < EMOTE_SHOWN_MS).slice(-4);
  if (recent.length === 0) return null;
  return (
    <ul className={styles['bubbles']} aria-label={t('chat.emotes')}>
      {recent.map((e, i) => (
        <li key={`${String(e.at)}-${String(i)}`} className={styles['bubble']}>
          <EmoteGlyph id={e.id} size="1.8rem" />
          <span>
            <b>{e.from === state.you ? t('chat.you') : e.name}</b>
            <span className="visually-hidden"> {t(`chat.emote.${e.id}`)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
